import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { ApplicationConfigurationService } from '@app/core/services/ApplicationConfiguration.service';
import { trustedWorkflowReturnRoute } from '@app/shared/workflow-navigation/workflow-return-route';
import type { HearingWorkflowLink } from './hearing-workflow-task-authority.service';

/**
 * The default preparation entry opens the exact retained task selected by the application.
 * Explicit source-record bookmarks remain editing/navigation intent, never command authority.
 */
export const hearingPreparationEntryGuard: CanActivateFn = route => {
  if (route.queryParamMap.get('view') === 'record' || !!route.fragment
    || ['section', 'details', 'legalReview'].some(key => route.queryParamMap.has(key))) return of(true);

  const rawId = route.parent?.paramMap.get('scheduledProceedingId') ?? route.paramMap.get('scheduledProceedingId');
  const proceedingId = rawId && /^[1-9]\d*$/.test(rawId) ? Number(rawId) : 0;
  if (!Number.isSafeInteger(proceedingId) || proceedingId <= 0) return of(true);

  const http = inject(HttpClient), config = inject(ApplicationConfigurationService), router = inject(Router);
  const navigationState = router.getCurrentNavigation()?.extras.state;
  const returnTo = trustedWorkflowReturnRoute(route.queryParamMap.get('returnTo'))
    ?? trustedWorkflowReturnRoute(navigationState?.['phmsReturn']?.url);
  return http.get<HearingWorkflowLink>(config.apiUrl(`/api/v1/phms/hearing-records/${proceedingId}/workflow`), {
    withCredentials: true,
    headers: new HttpHeaders({ 'skip-error-interceptor': 'yes', 'skip-loading-interceptor': 'yes' }),
  }).pipe(
    map(link => {
      if (!link || link.availability !== 'available'
        || !Number.isSafeInteger(link.workflowInstanceId) || link.workflowInstanceId! <= 0
        || !Number.isSafeInteger(link.occurrenceId) || link.occurrenceId! <= 0
        || link.route !== `/workflows/${link.workflowInstanceId}/steps/${link.occurrenceId}`) return true;
      const destination = router.parseUrl(link.route);
      if (returnTo) destination.queryParams = { returnTo };
      return new RedirectCommand(destination, { replaceUrl: true });
    }),
    // The existing record surface owns unavailable/denied recovery and independently rechecks
    // its reads. Returning an Observable lets the Router cancel superseded entry requests.
    catchError(() => of(true)),
  );
};
